import { useMemo } from 'react'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import {
  getBep20Contract,
  getErc721Contract,
  getNftMarketContract,
  getErc721CollectionContract,
  getGhostCollectionFactoryContract,
  getGhostFeeManagerContract,
  getGhostFeeManagerFactoryContract,
  getGhostSharePotContract,
  getGhostSharePotFactoryContract,
  getGhostPadFactoryContract,
  getGhostOrderProxyContract,
  getGhostBaseCollectionContract,
  getGhostWhitelistFactoryContract,
  getGhostWhitelistContract,
} from 'utils/contractHelpers'
import { getMulticallAddress } from 'utils/addressHelpers'

// Imports below migrated from Exchange useContract.ts
import { Contract } from '@ethersproject/contracts'
import { tokens } from 'config/constants/tokens'
import { ChainId } from '@ghost-nft/eth-ghost-sdk'
import ENS_PUBLIC_RESOLVER_ABI from '../config/abi/ens-public-resolver.json'
import ENS_ABI from '../config/abi/ens-registrar.json'
import { ERC20_BYTES32_ABI } from '../config/abi/erc20'
import ERC20_ABI from '../config/abi/erc20.json'
import WETH_ABI from '../config/abi/weth.json'
import multiCallAbi from '../config/abi/Multicall.json'
import { getContract, getProviderOrSigner, isAddress } from '../utils'

/**
 * Helper hooks to get specific contracts (by ABI)
 */

export const useERC20 = (address: string) => {
  const { library } = useActiveWeb3React()
  return useMemo(() => getBep20Contract(address, library.getSigner()), [address, library])
}

/**
 * @see https://docs.openzeppelin.com/contracts/3.x/api/token/erc721
 */
export const useERC721 = (address: string) => {
  const { library } = useActiveWeb3React()
  return useMemo(() => getErc721Contract(address, library.getSigner()), [address, library])
}

export const useNftMarketContract = () => {
  const { library } = useActiveWeb3React()
  return useMemo(() => getNftMarketContract(library.getSigner()), [library])
}

export const useGhostCollectionFactoryContract = () => {
  const { library } = useActiveWeb3React()
  return useMemo(() => getGhostCollectionFactoryContract(library.getSigner()), [library])
}
export const useGhostOrderProxyContract = () => {
  const { library } = useActiveWeb3React()
  return useMemo(() => getGhostOrderProxyContract(library.getSigner()), [library])
}
export const useGhostFeeManagerContract = (address: string) => {
  const { library } = useActiveWeb3React()
  return useMemo(() => getGhostFeeManagerContract(address, library.getSigner()), [library, address])
}
export const useGhostFeeManagerFactoryContract = () => {
  const { library } = useActiveWeb3React()
  return useMemo(() => getGhostFeeManagerFactoryContract(library.getSigner()), [library])
}
export const useGhostSharePotContract = (address: string) => {
  const { library } = useActiveWeb3React()
  return useMemo(() => getGhostSharePotContract(address, library.getSigner()), [library, address])
}
export const useGhostSharePotFactoryContract = () => {
  const { library } = useActiveWeb3React()
  return useMemo(() => getGhostSharePotFactoryContract(library.getSigner()), [library])
}
export const useGhostPadFactoryContract = () => {
  const { library } = useActiveWeb3React()
  return useMemo(() => getGhostPadFactoryContract(library.getSigner()), [library])
}

export const useGhostWhitelistFactoryContract = () => {
  const { library } = useActiveWeb3React()
  return useMemo(() => getGhostWhitelistFactoryContract(library.getSigner()), [library])
}

export const useGhostWhitelistContract = (address: string) => {
  const { library } = useActiveWeb3React()
  return useMemo(() => getGhostWhitelistContract(address, library.getSigner()), [address, library])
}

export const useGhostBaseCollectionContract = (collectionAddress: string) => {
  const { library } = useActiveWeb3React()
  return useMemo(
    () => getGhostBaseCollectionContract(collectionAddress, library.getSigner()),
    [collectionAddress, library],
  )
}

export const useErc721CollectionContract = (collectionAddress: string, withSignerIfPossible = true) => {
  const { library, account } = useActiveWeb3React()
  return useMemo(() => {
    return isAddress(collectionAddress)
      ? getErc721CollectionContract(
          withSignerIfPossible ? getProviderOrSigner(library, account) : null,
          collectionAddress,
        )
      : null
  }, [account, library, collectionAddress, withSignerIfPossible])
}

// Code below migrated from Exchange useContract.ts

// returns null on errors
function useContract(address: string | undefined, ABI: any, withSignerIfPossible = true): Contract | null {
  const { library, account } = useActiveWeb3React()

  return useMemo(() => {
    if (!address || !ABI || !library) return null
    try {
      return getContract(address, ABI, withSignerIfPossible ? getProviderOrSigner(library, account) : null)
    } catch (error) {
      console.error('Failed to get contract', error)
      return null
    }
  }, [address, ABI, library, withSignerIfPossible, account])
}

export function useTokenContract(tokenAddress?: string, withSignerIfPossible?: boolean): Contract | null {
  return useContract(tokenAddress, ERC20_ABI, withSignerIfPossible)
}

export function useWETHContract(withSignerIfPossible?: boolean): Contract | null {
  return useContract(tokens.weth.address, WETH_ABI, withSignerIfPossible)
}

export function useENSRegistrarContract(withSignerIfPossible?: boolean): Contract | null {
  const { chainId } = useActiveWeb3React()
  let address: string | undefined
  if (chainId) {
    // eslint-disable-next-line default-case
    switch (chainId) {
      case ChainId.ETHERNET:
      case ChainId.BSCNET:
        address = '0x00000000000C2E074eC69A0dFb2997BA6C7d2e1e'
        break
    }
  }
  return useContract(address, ENS_ABI, withSignerIfPossible)
}

export function useENSResolverContract(address: string | undefined, withSignerIfPossible?: boolean): Contract | null {
  return useContract(address, ENS_PUBLIC_RESOLVER_ABI, withSignerIfPossible)
}

export function useBytes32TokenContract(tokenAddress?: string, withSignerIfPossible?: boolean): Contract | null {
  return useContract(tokenAddress, ERC20_BYTES32_ABI, withSignerIfPossible)
}

export function useMulticallContract(): Contract | null {
  return useContract(getMulticallAddress(), multiCallAbi, false)
}
